import {
  FC,
  cloneElement,
  Fragment,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  GridProps,
  IconButton,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { renderField } from "components/dynamicForm/utils/fieldRenderer";
import { FieldMetaDataType } from "components/dynamicForm/";
import { useFieldArray, formFieldAtom } from "packages/form";
import { extendFieldTypes } from "components/dynamicForm/utils/extendedFieldTypes";
import { extendedMetaData } from "components/dynamicForm/extendedTypes";
import { attachMethodsToMetaData } from "components/dynamicForm/utils/attachMethodsToMetaData";
import { MoveSequenceToRender } from "components/dynamicForm/utils/fixSequenceInMetaData";
import { MetaDataType } from "components/dynamicForm";
import { useStyles } from "./style";
import { useRecoilCallback } from "recoil";
import { cloneDeep } from "lodash-es";
import { useTranslation } from "react-i18next";
import { usePropertiesConfigContext } from "components/context/propertiesConfig/customProperties";
import { useAuthContext } from "components/context/authContext/authContext";
import { useStyles as useDialogStyles } from "components/custom/style";
import { GradientButton } from "components/styledComponent/button";
import HelpIcon from "@mui/icons-material/Help";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
export interface ArrayField2Props {
  fieldKey: string;
  name: string;
  label: string;
  //just to satisfy typescript no use
  enableGrid: boolean;
  GridProps?: GridProps;
  _fields: FieldMetaDataType[];
  componentProps?: any;
  removeRowFn?: any;
  addRowFn?: any;
  arrayFieldIDName?: string;
  dependentFields?: string | string[];
  shouldExclude?: any;
  fixedRows?: boolean;
  isDisplayCount?: boolean;
  isCustomStyle?: any;
  getFixedRowsCount?: any;
  onFormButtonClickHandel?: any;
  disagreeButtonName: any;
  agreeButtonName: any;
  errorTitle: string;
  displayCountName: string;
  runExternalFunction?: boolean;
  isRemoveButton?: boolean;
  onFormDataChange?: any;
  changeRowOrder?: boolean;
  noRecordMessage?: string;
  hideRemoveIconOnSingleRecord?: boolean;
}

const metaDataTransform = (
  metaData: MetaDataType,
  t,
  authState,
  customParameters,
): MetaDataType => {
  metaData = extendFieldTypes(
    metaData,
    extendedMetaData,
    t,
    authState,
    customParameters,
  );
  metaData = attachMethodsToMetaData(metaData);
  metaData = MoveSequenceToRender(metaData);
  return metaData;
};

export const ArrayField2: FC<ArrayField2Props> = ({
  name,
  label,
  _fields,
  GridProps,
  enableGrid,
  componentProps = {},
  removeRowFn,
  addRowFn,
  arrayFieldIDName,
  dependentFields,
  shouldExclude,
  fixedRows,
  isDisplayCount,
  isCustomStyle,
  getFixedRowsCount,
  onFormButtonClickHandel,
  onFormDataChange,
  disagreeButtonName,
  agreeButtonName,
  errorTitle,
  displayCountName,
  isRemoveButton,
  changeRowOrder,
  noRecordMessage,
  hideRemoveIconOnSingleRecord = true,
}) => {
  // let currentFieldsMeta = JSON.parse(
  //   JSON.stringify(_fields)
  // ) as FieldMetaDataType[];
  const { t } = useTranslation();
  const customParameters = usePropertiesConfigContext();
  const { authState } = useAuthContext();
  let currentFieldsMeta = cloneDeep(_fields) as FieldMetaDataType[];
  const classes = useStyles();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [dialogMsg, setDialogMsg] = useState("");
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  let metaData = { form: {}, fields: currentFieldsMeta } as MetaDataType;
  const transformedMetaData = useRef<MetaDataType | null>(null);
  if (transformedMetaData.current === null) {
    transformedMetaData.current = metaDataTransform(
      metaData,
      t,
      authState,
      customParameters,
    );
  }
  const template = useRef(
    transformedMetaData?.current?.fields?.reduce((accum, one) => {
      accum[one.name] = "";
      return accum;
    }, {}),
  );

  const currentMetaToObj = useRef(
    transformedMetaData?.current?.fields?.reduce((accum, one) => {
      accum[one.name] = one;
      return accum;
    }, {}),
  );

  const {
    renderRows,
    getAllRowsValues,
    unshift,
    isSubmitting,
    formState,
    formName,
    excluded,
    push,
  } = useFieldArray({
    arrayFieldName: name,
    template: template.current,
    dependentFields: dependentFields,
    shouldExclude: shouldExclude,
    getFixedRowsCount: getFixedRowsCount,
  });

  /*eslint-disable react-hooks/exhaustive-deps*/
  const addNewRow = useCallback(() => {
    if (typeof addRowFn === "function") {
      let result = addRowFn(getAllRowsValues());
      let allow = false;
      let reason = "Cannot add a new Row";
      if (typeof result === "boolean") {
        allow = result;
      }
      if (typeof result === "object") {
        allow = result?.allow ?? false;
        reason = result?.reason ?? "Cannot add a new Row";
      }
      if (allow) {
        Boolean(changeRowOrder) ? push() : unshift();
      } else {
        setShowAddDialog(true);
        setDialogMsg(reason);
      }
    } else {
      Boolean(changeRowOrder) ? push() : unshift();
    }
  }, [unshift, getAllRowsValues]);

  if (excluded) {
    return null;
  }

  let rows = renderRows(({ row, removeFn, rowIndex, fields, totalRows }) => {
    const oneRow = fields.map((field) => {
      const currentFieldMetaData = currentMetaToObj.current[field];
      if (!Boolean(currentFieldMetaData)) {
        return null;
      }

      currentFieldMetaData["onFormButtonClickHandel"] = onFormButtonClickHandel;
      currentFieldMetaData["onFormDataChange"] = onFormDataChange;

      let newMTdata;
      if (rowIndex === 0) {
        newMTdata = { ...currentFieldMetaData };
      } else {
        newMTdata = { ...currentFieldMetaData, label: null };
      }

      const component = renderField(
        isCustomStyle ? newMTdata : currentFieldMetaData,
        //@ts-ignore
        {},
        name,
        componentProps,
      );
      let clonedComponent = cloneElement(component, {
        fieldKey: row.cells[field].key,
        name: row.cells[field].name,
        label: t(component?.props?.label),
        placeholder: t(component?.props?.placeholder),
      });
      return <Fragment key={row.cells[field].key}>{clonedComponent}</Fragment>;
    });
    return (
      <ArrayFieldRow
        key={row.fieldIndexKey}
        fieldKey={row.cells[arrayFieldIDName ?? ""]?.key ?? ""}
        getAllRowsValues={getAllRowsValues}
        oneRow={oneRow}
        classes={classes}
        removeFn={removeFn}
        rowIndex={rowIndex}
        removeRowFn={removeRowFn}
        row={row}
        totalRows={totalRows}
        isSubmitting={isSubmitting}
        formState={formState}
        formName={formName}
        arrayFieldIDName={arrayFieldIDName}
        arrayFieldName={name}
        fixedRows={fixedRows}
        isDisplayCount={isDisplayCount}
        isCustomStyle={isCustomStyle}
        disagreeButtonName={disagreeButtonName}
        agreeButtonName={agreeButtonName}
        errorTitle={errorTitle}
        displayCountName={displayCountName}
        isRemoveButton={isRemoveButton}
        selectedRowIndex={selectedRowIndex}
        setSelectedRowIndex={setSelectedRowIndex}
        hideRemoveIconOnSingleRecord={hideRemoveIconOnSingleRecord}
      />
    );
  });

  const cardHeaderTitleStyle = {
    background: "var(--theme-color5)",
    ml: 1.5,
    mt: 1,
    p: 1,
    color: "var(--theme-color2)",
  };
  let result = (
    <Fragment>
      <Card className={classes.arrayRowCard}>
        <CardHeader
          style={{ padding: "0px" }}
          sx={{
            "& .MuiCardHeader-title": label ? cardHeaderTitleStyle : null,
            "& .MuiCardHeader-action": {
              // my: 1,
              // mr: 0,
            },
          }}
          title={label}
          action={
            !Boolean(fixedRows) ? (
              <IconButton
                style={{ padding: "5px 10px 0px 10px" }}
                onClick={addNewRow}
                disabled={isSubmitting}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            ) : null
          }
        />
        <CardContent
          className={classes.arrayRowCardContent}
          style={{ paddingTop: "0px", paddingBottom: "16px" }}
        >
          <Grid
            container
            item
            spacing={1}
            xs={12}
            md={12}
            sm={12}
            style={{ marginTop: "2px" }}
          >
            {rows}
            {rows.length <= 0 ? (
              <Typography>
                {noRecordMessage
                  ? t(noRecordMessage)
                  : t("NoRecordsFoundclicktheaddbuttontoaddone")}
              </Typography>
            ) : null}
          </Grid>
        </CardContent>
      </Card>
      <Dialog
        open={showAddDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>{dialogMsg}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddDialog(false);
              setDialogMsg("");
            }}
            sx={{ color: "var(--theme-color1) !important" }}
          >
            {t("Ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
  if (Boolean(enableGrid)) {
    return (
      <Grid container {...GridProps} key={name}>
        {result}
      </Grid>
    );
  } else {
    return <Fragment key={name}>{result}</Fragment>;
  }
};

export const ArrayFieldRow = ({
  row,
  getAllRowsValues,
  fieldKey,
  oneRow,
  classes,
  removeFn,
  rowIndex,
  removeRowFn,
  totalRows,
  isSubmitting,
  formState,
  formName,
  arrayFieldIDName,
  arrayFieldName,
  fixedRows,
  isDisplayCount = true,
  isCustomStyle = false,
  disagreeButtonName,
  agreeButtonName,
  errorTitle,
  displayCountName,
  isRemoveButton,
  selectedRowIndex,
  setSelectedRowIndex,
  hideRemoveIconOnSingleRecord,
}) => {
  const { t } = useTranslation();
  const dialogClasses = useDialogStyles();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const dialogAccept = useRecoilCallback(
    ({ snapshot }) =>
      async () => {
        const loader = snapshot.getLoadable(
          formFieldAtom(`${formName}/${fieldKey}`),
        );
        if (loader.state === "hasValue") {
          const field = loader.contents;
          if (Boolean(field.value)) {
            try {
              setLoading(true);
              await Promise.resolve(
                removeRowFn(
                  { ...formState, [arrayFieldIDName]: field.value },
                  arrayFieldName,
                ),
              );
              setLoading(false);
              setError("");
              setSuccess(true);
            } catch (e) {
              console.log(e);
              setLoading(false);
              //@ts-ignore
              setError(e?.error_msg ?? "Unkown error occured");
            }
          } else {
            setSuccess(true);
          }
        }
      },
    [setError, setSuccess, setLoading],
  );
  const dialogReject = useCallback(
    (flag = false) => {
      if (success || flag) {
        removeFn(rowIndex);
      } else {
        setIsDialogOpen(false);
        setTimeout(() => {
          setLoading(false);
          setError("");
          setSuccess(false);
        }, 1);
      }
    },
    [
      setIsDialogOpen,
      setLoading,
      setError,
      setSuccess,
      success,
      removeFn,
      rowIndex,
    ],
  );
  const dialogOpen = useCallback(() => {
    setIsDialogOpen(true);
  }, [setIsDialogOpen]);

  let finalClass;
  if (Boolean(isCustomStyle)) {
    if (rowIndex !== 0) {
      finalClass = classes.newArrayRowContainer;
    } else {
      finalClass = classes.newSecondArrayRowContainer;
    }
  } else if (Boolean(fixedRows)) {
    finalClass = classes.newSecondArrayRowContainer;
  } else {
    finalClass = classes.arrayRowContainer;
  }

  const _rowColor = getAllRowsValues()?.[arrayFieldName]?.[rowIndex]?._rowColor;

  const handleWrapperClick = () => {
    setSelectedRowIndex(rowIndex);
    const allRowsValues = getAllRowsValues();

    // Create an array to store the keys needed to access the value in allRowsValues
    const getFieldValue = (fieldName) => {
      const parts = fieldName.replace(/\]/g, "").split(/\[|\./);
      let value = allRowsValues;

      parts.forEach((part) => {
        if (value && part in value) {
          value = value[part];
        } else {
          value = undefined;
        }
      });

      return value;
    };

    const wrapperData = Object.keys(row.cells).reduce((acc, key) => {
      const fieldName = row.cells[key].name;

      // Extract the value of the field from allRowsValues
      const fieldValue = getFieldValue(fieldName);
      acc[key] = fieldValue;
      return acc;
    }, {});
    if (typeof formState?.onArrayFieldRowClickHandle === "function") {
      formState?.onArrayFieldRowClickHandle(wrapperData);
    }
    if (typeof formState?.onArrayFieldRowDoubleClickHandle === "function") {
      formState?.onArrayFieldRowDoubleClickHandle(wrapperData);
    }
  };
  const colorMap = {
    INFO: "dodgerblue",
    SUCCESS: "green",
    WARNING: "orange",
    ERROR: "#f44336",
    CONFIRM: "#474e5b",
  };
  return (
    <Fragment key={row.fieldIndexKey}>
      {Boolean(isDisplayCount) ? (
        <>
          {displayCountName ? (
            <Typography
              gutterBottom
              className={
                isCustomStyle
                  ? classes.arrayRowCountCustomStyle
                  : fixedRows
                    ? classes.arrayRowCountFixedRows
                    : classes.arrayRowCount
              }
            >
              {`${displayCountName} ${rowIndex + 1} of ${totalRows}`}
            </Typography>
          ) : (
            <Typography
              gutterBottom
              className={
                isCustomStyle
                  ? classes.arrayRowCountCustomStyle
                  : fixedRows
                    ? classes.arrayRowCountFixedRows
                    : classes.arrayRowCount
              }
            >
              {`${rowIndex + 1} of ${totalRows}`}
            </Typography>
          )}
        </>
      ) : null}
      <div
        onClick={formState?.onArrayFieldRowClickHandle && handleWrapperClick}
        onDoubleClick={
          formState?.onArrayFieldRowDoubleClickHandle && handleWrapperClick
        }
        className={
          selectedRowIndex === rowIndex
            ? classes?.arrayFieldSelected
            : classes?.arrayFieldUnselected
        }
      >
        <Grid
          container
          item
          xs={12}
          md={12}
          sm={12}
          spacing={1}
          className={finalClass}
          sx={{
            background: `${
              Boolean(_rowColor) && typeof _rowColor === "string"
                ? _rowColor
                : "none"
            }`,
          }}
        >
          {oneRow}
          {(typeof removeFn === "function" && !Boolean(fixedRows)) ||
          Boolean(isRemoveButton) ? (
            <>
              {totalRows > 1 || !hideRemoveIconOnSingleRecord ? (
                <IconButton
                  onClick={dialogOpen}
                  className={classes.arrayRowRemoveBtn}
                  disabled={isSubmitting}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              ) : null}
            </>
          ) : null}
        </Grid>
      </div>
      {fixedRows && rowIndex + 1 < totalRows ? (
        <Divider
          sx={{ backgroundColor: "rgba(0, 0, 0, 0.6)", width: "100%" }}
        />
      ) : null}
      <Dialog
        open={isDialogOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          className={dialogClasses.dialogTitleClass}
          style={{
            background: success ? colorMap["SUCCESS"] : colorMap["CONFIRM"],
          }}
        >
          {success ? t("Success") : t("Confirmation")}
        </DialogTitle>
        <DialogContent
          sx={{
            padding: ".75rem 10px 1.5rem 10px !important",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <Box>
            {success ? (
              <CheckCircleIcon
                fontSize="large"
                style={{ color: colorMap["SUCCESS"] }}
              />
            ) : (
              <HelpIcon
                fontSize="large"
                style={{ color: colorMap["CONFIRM"] }}
              />
            )}
          </Box>
          <DialogContentText>
            <Typography color={"black"}>
              {success
                ? t("Recordsuccessfullydeleted")
                : loading
                  ? t("Pleasewaitdeletingrecord")
                  : Boolean(error)
                    ? error
                    : errorTitle
                      ? t(errorTitle)
                      : t("AreyouSureyouwanttodeletethisrecord")}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <GradientButton
            onClick={() => dialogReject(true)}
            sx={{
              background: colorMap["CONFIRM"],
              "&:hover": { background: colorMap["CONFIRM"] },
            }}
          >
            {agreeButtonName ? t(agreeButtonName) : t("Yes")}
          </GradientButton>
          <GradientButton
            onClick={() => dialogReject(false)}
            sx={{
              background: colorMap["CONFIRM"],
              "&:hover": { background: colorMap["CONFIRM"] },
            }}
          >
            {disagreeButtonName ? t(disagreeButtonName) : t("No")}
          </GradientButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
